import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertLightAdmin } from "@/lib/admin-guard.server";
import { logAdminAction } from "@/lib/audit.server";
import { MAIL_ORIGIN_TOKEN, MAIL_TRACK_TOKEN } from "@/lib/mail-logo";
import { LIVE_ORIGIN } from "@/lib/site-origin";

/**
 * Sparad mejl-HTML innehåller platshållare som byts först vid utskick. Utan
 * bytet blir logotypens adress ogiltig när vi tittar på mejlet i admin.
 * Spårningspixeln tas bort så att en admintitt aldrig loggas som en öppning.
 */
function mailHtmlForAdmin(html: string): string {
  return html
    .split(MAIL_ORIGIN_TOKEN)
    .join(LIVE_ORIGIN)
    .replace(/<img[^>]*mailbild\/px-[^>]*>/gi, "")
    .split(MAIL_TRACK_TOKEN)
    .join("");
}

export const openEnrollmentView = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().max(100), fromAdmin: z.boolean().optional() }).parse(data),
  )
  .handler(async ({ data }) => {
    if (!z.string().uuid().safeParse(data.token).success) {
      return {
        state: "missing" as const,
        locale: "sv" as const,
        submittedAt: null,
        applicationNumber: null,
        students: [],
        guardians: [],
        wantsRelocationConsultation: false,
      };
    }
    const { loadEnrollmentView } = await import("@/lib/enrollment-view.server");
    // Öppnar vi sidan själva från admin räknas det inte som ett besök av familjen.
    return loadEnrollmentView(data.token, data.fromAdmin !== true);
  });

/**
 * Sidan /visa-anmalan är publik och loggar besöket innan webbläsaren hunnit
 * läsa sessionen. Öppnar en inloggad medarbetare länken (utan ?admin=1)
 * rättar vi besöket i efterhand så loggen visar vem av oss som tittade,
 * i stället för att det ser ut som ett familjebesök.
 */
export const claimEnrollmentViewAsStaff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ token: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    let staff = false;
    for (const role of ["admin", "staff"] as const) {
      const { data: hasRole } = await context.supabase.rpc("has_role", {
        _user_id: context.userId,
        _role: role,
      });
      if (hasRole === true) { staff = true; break; }
    }
    if (!staff) return { ok: false as const };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: link } = await supabaseAdmin
      .from("enrollment_view_links")
      .select("id")
      .eq("token", data.token)
      .maybeSingle();
    if (!link) return { ok: false as const };

    const since = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    await supabaseAdmin
      .from("enrollment_view_events")
      .update({ viewer_user_id: context.userId, viewer_kind: "admin" })
      .eq("link_id", link.id)
      .is("viewer_user_id", null)
      .gte("viewed_at", since);
    return { ok: true as const };
  });

export const getEnrollmentViewAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/ansokningar");
    const { data: row } = await context.supabase
      .from("enrollment_applications")
      .select("application_group_id")
      .eq("id", data.id)
      .maybeSingle();
    if (!row?.application_group_id) throw new Error("Ansökan saknar grupp-id.");

    await logAdminAction(context as never, {
      action: "application.open",
      entity: "enrollment_application",
      entityId: data.id,
      summary: "Öppnade ansökan i admin.",
      meta: { applicationGroupId: row.application_group_id },
    });

    const { ensureEnrollmentViewLink, enrollmentViewUrl, loadEnrollmentView } = await import("@/lib/enrollment-view.server");
    const link = await ensureEnrollmentViewLink(row.application_group_id);
    const [eventsResult, countResult] = await Promise.all([
      context.supabase
        .from("enrollment_view_events")
        .select("id, viewed_at, user_agent, viewer_kind, viewer_user_id")
        .eq("link_id", link.id)
        .order("viewed_at", { ascending: false })
        .limit(200),
      context.supabase
        .from("enrollment_view_events")
        .select("id, viewer_kind")
        .eq("link_id", link.id),
    ]);
    if (eventsResult.error || countResult.error) throw new Error("Besökshistoriken kunde inte hämtas.");
    const events = eventsResult.data ?? [];
    const { userNamesById } = await import("@/lib/user-names.server");
    const names = await userNamesById(events.map((e) => e.viewer_user_id).filter((v): v is string => Boolean(v)));
    const views = events.map((e) => ({
      id: e.id,
      viewed_at: e.viewed_at,
      user_agent: e.user_agent,
      viewerKind: (e.viewer_kind ?? "family") as string,
      viewerName: e.viewer_user_id
        ? (names.get(e.viewer_user_id) ?? "Okänd användare")
        : e.viewer_kind === "admin"
          ? "Okänd"
          : "Familjen",
    }));
    const all = countResult.data ?? [];
    const familyCount = all.filter((e) => (e.viewer_kind ?? "family") === "family").length;
    const adminCount = all.length - familyCount;
    const preview = await loadEnrollmentView(link.token, false);

    // Mejlen familjen har fått – både på den här eleven och på hela inskicket.
    const { data: mailRows } = await context.supabase
      .from("email_log")
      .select("id, kind, recipient, subject, status, error, meta, created_at, delivered_at, opened_at, bounced_at")
      .or(`meta->>application_id.eq.${data.id},meta->>application_group_id.eq.${row.application_group_id}`)
      .order("created_at", { ascending: false })
      .limit(20);
    const emails = (mailRows ?? []).map((mail) => {
      const meta = (mail.meta ?? {}) as Record<string, unknown>;
      return {
        id: mail.id,
        kind: mail.kind,
        recipient: mail.recipient,
        subject: mail.subject,
        status: mail.status,
        error: mail.error,
        created_at: mail.created_at,
        delivered_at: mail.delivered_at,
        opened_at: mail.opened_at,
        bounced_at: mail.bounced_at,
        resent: meta["resent"] === true,
        html: typeof meta["html"] === "string" ? mailHtmlForAdmin(meta["html"] as string) : null,
        text: typeof meta["text"] === "string" ? (meta["text"] as string) : null,
      };
    });

    return {
      active: link.active,
      url: enrollmentViewUrl(link.token),
      token: link.token,
      views,
      viewCount: familyCount,
      adminViewCount: adminCount,
      latestViewAt: views.find((v) => v.viewerKind === "family")?.viewed_at ?? null,
      preview,
      emails,
    };

  });

export const setEnrollmentViewActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid(), active: z.boolean() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/ansokningar");
    const { data: row } = await context.supabase
      .from("enrollment_applications")
      .select("application_group_id, student_first_name, student_last_name")
      .eq("id", data.id)
      .maybeSingle();
    if (!row?.application_group_id) throw new Error("Ansökan hittades inte.");
    const { ensureEnrollmentViewLink } = await import("@/lib/enrollment-view.server");
    const link = await ensureEnrollmentViewLink(row.application_group_id);
    const { error } = await context.supabase
      .from("enrollment_view_links")
      .update({ active: data.active, revoked_at: data.active ? null : new Date().toISOString() })
      .eq("id", link.id);
    if (error) throw new Error("Visningslänken kunde inte ändras.");
    await logAdminAction(context as never, {
      action: data.active ? "application.view_link_enable" : "application.view_link_disable",
      entity: "enrollment_application",
      entityId: data.id,
      severity: data.active ? "info" : "warning",
      summary: `${data.active ? "Aktiverade" : "Stängde av"} visningslänken för ${row.student_first_name} ${row.student_last_name}`,
    });
    return { ok: true as const };
  });